import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { RosterMemberService } from './roster-member.service';
import { RosterModalService } from './roster-modal.service';

/**
 * Minimal interface that the component must satisfy to use RosterMemberOpsService.
 * Allows the service to mutate component state and call component methods without
 * importing the full component class (which would create a circular dependency).
 */
export interface IRosterOpsHost {
  // identifiers
  id: string;
  // mutable state
  teams: any[];
  teamsData: any;
  filterData: any;
  selectedRosterMembers: any[];
  selectedFilterMbr: any[];
  allTeamIdsTtl: any[];
  defaultShowingMembers: number;
  roasterdefaultCounter: number;
  currentPg: number;
  searchby: string;
  newUseremail: string;
  gridHirView: string;
  teamLoaders: { [key: string]: boolean };
  loading: any;
  team_name: any;
  destroy$: Subject<void>;
  // modal service — modal/popup state lives here
  modalSvc: RosterModalService;
  // callbacks into the component
  hirerachicalData(): void;
  roasterMembers(count?: number, page?: number): void;
}

@Injectable({ providedIn: 'root' })
export class RosterMemberOpsService {

  constructor(private rosterMemberService: RosterMemberService) {}

  private getMemberEmail(mbr: any): string {
    return mbr?.default_identity?.email || mbr?.default_profile?.email || '';
  }

  // ── add roster member to team ──────────────────────────────────────────────

  async addRosterMemberInTeam(host: IRosterOpsHost): Promise<void> {
    let data: any;
    let members_attributes = {};
    let checkedMemberEmail = [];
    if (
      (host.searchby == 'invitation' &&
        host.modalSvc.selectedInvitation != 'Select Invitation') ||
      host.filterData
    ) {
      data = host.filterData;
      host.selectedFilterMbr.forEach((mbr) => {
        let itemExist = false;
        host.filterData.forEach((fmbr) => {
          if (mbr.id == fmbr.id) {
            itemExist = true;
          }
        });
        if (!itemExist) {
          data.push(mbr);
        }
      });
    } else if (host.selectedRosterMembers.length > 0) {
      data = host.selectedRosterMembers;
    } else {
      data = host.teamsData.members;
    }

    await data.forEach((mbr, mbrIndex) => {
      if (mbr.checked) {
        const email = this.getMemberEmail(mbr);
        checkedMemberEmail.push(email);
        mbr.checked = false;
        members_attributes[mbrIndex] = { email, role_id: 3 };
      }
    });

    let teamCheckedCounter = 0;
    await new Promise((resolve) => {
      setTimeout(async () => {
        resolve(
          await host.teams.forEach(async (team, index) => {
            if (team.checked === true) {
              teamCheckedCounter++;
              host.teamLoaders[team.id] = true;
              this.rosterMemberService.addMembersToTeam(team.id, members_attributes)
                .pipe(takeUntil(host.destroy$)).subscribe(
                  (res: any) => {
                    document.querySelector('body').classList.remove('oh');
                    host.teams[index].memberships = res?.team?.memberships;
                    host.teams[index].memberships_count = res?.team?.memberships_count;
                    host.teamLoaders[team.id] = false;
                    teamCheckedCounter = teamCheckedCounter - 1;
                    if (teamCheckedCounter == 0) {
                      host.roasterMembers(host.roasterdefaultCounter, host.currentPg);
                    }
                    if (host.gridHirView == 'hierar') {
                      host.hirerachicalData();
                    }
                  },
                  (err) => {
                    host.teamLoaders[team.id] = false;
                    Swal.fire({ title: 'Oops!', text: err?.error?.errors, icon: 'error', timer: 2000, showConfirmButton: false });
                    host.modalSvc.addTeamPopup = false;
                    host.modalSvc.membersDetails4NewTeam = null;
                  }
                );
            }
          })
        );
      }, 100);
    }).then(() => {
      host.allTeamIdsTtl = [];
      host.teams.forEach((team, tmInd) => {
        if (team.checked === true) {
          team.checked = false;
          data.forEach((mbr) => {
            const email = this.getMemberEmail(mbr);
            if (checkedMemberEmail.includes(email)) {
              host.teams[tmInd].memberships.push({ email: mbr.email });
              mbr.teams_names[team.id] = team.name;
            }
          });
          mbr => mbr.checked = false;
          host.teams[tmInd].memberships_count =
            host.teams[tmInd].memberships_count +
            checkedMemberEmail.filter(String).length;
        }
        host.teamLoaders[team.id] = false;
        host.allTeamIdsTtl.push({
          teamId: team.id,
          showMember: host.defaultShowingMembers,
          teamName: team.name,
          memberships_count: team.memberships_count,
        });
      });

      host.selectedFilterMbr.forEach((mbr) => {
        data.pop(mbr);
      });

      if (checkedMemberEmail.filter(String).length <= 1) {
        Swal.fire({ title: 'Great!', text: 'Member is added successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ title: 'Great!', text: 'Members are added successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
      }
      host.modalSvc.addTeamPopup = false;
      host.modalSvc.membersDetails4NewTeam = null;
      host.selectedFilterMbr = [];
      host.selectedRosterMembers = [];
    });
  }

  // ── add member from invitation ─────────────────────────────────────────────

  async addInvitationMem(host: IRosterOpsHost): Promise<void> {
    let data: any;
    if (
      host.searchby == 'invitation' &&
      host.modalSvc.selectedInvitation != 'Select Invitation'
    ) {
      data = host.filterData;
    } else {
      data = host.teamsData.members;
    }
    data?.forEach(async (mem) => {
      if (mem?.checked) {
        mem.checked = false;
        host.modalSvc.membersDetails4NewTeam = mem;
        await host.teams.forEach(async (team, index) => {
          let listMembers = { team_id: team.id, membership_role_id: 3 };
          let identity_id = host.modalSvc.membersDetails4NewTeam?.identity_id
            ? host.modalSvc.membersDetails4NewTeam?.identity_id
            : host.modalSvc.membersDetails4NewTeam?.default_identity?.id;
          if (team?.checked == true) {
            this.rosterMemberService.addMemberToTeamByIdentity(identity_id, team.id, listMembers.membership_role_id)
              .pipe(takeUntil(host.destroy$)).subscribe(
                (result: any) => {
                  result.membership['teams_names'] = host.modalSvc.membersDetails4NewTeam?.teams_names;
                  Object?.assign(result?.membership?.teams_names, { [team.id]: team.name });
                  host.teams[index]?.memberships?.push(result.membership);
                  host.teams[index].memberships_count += 1;

                  host.teamsData.members.forEach((mem, rInd) => {
                    if (
                      host.modalSvc.membersDetails4NewTeam?.id == mem?.id ||
                      host.modalSvc.membersDetails4NewTeam.member_id == mem.id ||
                      identity_id == mem?.default_identity?.id
                    ) {
                      host.teamsData.members[rInd].teams_names[team.id] = team.name;
                      host.teamsData.members[rInd].non_admin_unassgined_teams_count += 1;
                      if (host.filterData) {
                        host.filterData.forEach((fltrData, fltrInd) => {
                          if (fltrData.default_identity.id == mem.default_identity.id) {
                            host.filterData[fltrInd] = mem;
                          }
                        });
                      }
                    }
                  });
                  host.allTeamIdsTtl = [];
                  host.teams.forEach((tm) => {
                    tm.memberships.forEach((mbr) => {
                      if (
                        host.modalSvc.membersDetails4NewTeam?.id == mbr?.id ||
                        host.modalSvc.membersDetails4NewTeam.id == mbr.member_id ||
                        host.modalSvc.membersDetails4NewTeam.member_id == mbr.member_id
                      ) {
                        mbr.teams_names = result.membership.teams_names;
                      }
                    });
                    host.allTeamIdsTtl.push({
                      teamId: tm.id,
                      showMember: host.defaultShowingMembers,
                      teamName: tm.name,
                      memberships_count: tm.memberships_count,
                    });
                  });

                  if (host.filterData != '') {
                    host.filterData.forEach((mem) => {
                      if (identity_id == mem?.default_identity?.id) {
                        mem.non_admin_unassgined_teams_count += 1;
                        mem.teams_names[team.id] = team.name;
                      }
                    });
                  }

                  Swal.fire({ title: 'Great!', text: 'Members were added successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
                  host.modalSvc.addTeamPopup = false;
                  host.modalSvc.membersDetails4NewTeam = null;
                  team.checked = false;
                  host.loading = false;
                },
                (error) => {
                  if (error.error.errors) {
                    Swal.fire({ title: 'Oops!', text: `${error.error.errors}`, icon: 'error', timer: 2000, showConfirmButton: false });
                  }
                  host.loading = false;
                }
              );
          } else if (team?.checked == -1) {
            team.memberships.forEach(async (element) => {
              if (element.identity_id == identity_id) {
                host.modalSvc.membersDetails4NewTeam.tempId = host.modalSvc.membersDetails4NewTeam.id;
                host.modalSvc.membersDetails4NewTeam.id = element.id;
                await this.removedMember(host.modalSvc.membersDetails4NewTeam, team.id, host);
                team.checked = false;
              }
            });
          }
        });
      }
    });
  }

  // ── add members to team (dispatcher) ──────────────────────────────────────

  async addTeamMembers(host: IRosterOpsHost): Promise<void> {
    document.querySelector('body').classList.remove('oh');
    if (host.modalSvc.invitePopup || host.modalSvc.addToTeamBtn) {
      await this.addInvitationMem(host);
    } else {
      host.modalSvc.checkChanges = -1;
      await host.teams.forEach(async (team, index) => {
        let listMembers = { team_id: team.id, membership_role_id: 3 };
        let identity_id = host.modalSvc.membersDetails4NewTeam?.identity_id
          ? host.modalSvc.membersDetails4NewTeam?.identity_id
          : host.modalSvc.membersDetails4NewTeam?.default_identity?.id;
        if (team?.checked == true) {
          this.rosterMemberService.addMemberToTeamByIdentity(identity_id, team.id, listMembers.membership_role_id)
            .pipe(takeUntil(host.destroy$)).subscribe(
              (result: any) => {
                result.membership['teams_names'] = host.modalSvc.membersDetails4NewTeam.teams_names;
                Object.assign(result.membership.teams_names, { [team.id]: team.name });
                host.teams[index].memberships.push(result.membership);
                host.teams[index].memberships_count += 1;

                host.teamsData.members.forEach((mem, rInd) => {
                  if (
                    host.modalSvc.membersDetails4NewTeam?.id == mem?.id ||
                    host.modalSvc.membersDetails4NewTeam.member_id == mem.id ||
                    identity_id == mem?.default_identity?.id
                  ) {
                    host.teamsData.members[rInd].teams_names[team.id] = team.name;
                    host.teamsData.members[rInd].non_admin_unassgined_teams_count += 1;
                    if (host.filterData) {
                      host.filterData.forEach((fltrData, fltrInd) => {
                        if (fltrData.default_identity.id == mem.default_identity.id) {
                          host.filterData[fltrInd] = mem;
                        }
                      });
                    }
                  }
                });
                host.allTeamIdsTtl = [];
                host.teams.forEach((tm) => {
                  tm.memberships.forEach((mbr) => {
                    if (
                      host.modalSvc.membersDetails4NewTeam?.id == mbr?.id ||
                      host.modalSvc.membersDetails4NewTeam.id == mbr.member_id ||
                      host.modalSvc.membersDetails4NewTeam.member_id == mbr.member_id
                    ) {
                      mbr.teams_names = result.membership.teams_names;
                    }
                  });
                  host.allTeamIdsTtl.push({
                    teamId: tm.id,
                    showMember: host.defaultShowingMembers,
                    teamName: tm.name,
                    memberships_count: tm.memberships_count,
                  });
                });

                if (host.filterData != '') {
                  host.filterData.forEach((mem) => {
                    if (identity_id == mem?.default_identity?.id) {
                      mem.non_admin_unassgined_teams_count += 1;
                      mem.teams_names[team.id] = team.name;
                    }
                  });
                }

                if (host.gridHirView == 'hierar') {
                  host.hirerachicalData();
                }

                Swal.fire({ title: 'Great!', text: 'Members were added successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
                host.modalSvc.addTeamPopup = false;
                team.checked = false;
                host.modalSvc.addToTeamBtn = false;
                host.loading = false;
              },
              (error) => {
                if (error.error.errors) {
                  Swal.fire({ title: 'Oops!', text: `${error.error.errors}`, icon: 'error', timer: 2000, showConfirmButton: false });
                }
                host.loading = false;
              }
            );

          if (host.gridHirView == 'dirView') {
            host.hirerachicalData();
          }
        } else if (team?.checked == -1) {

          const removedMemberProcess = (teamMember: any) => {
            teamMember.forEach(async (element, index) => {
              if (element.identity_id == identity_id) {
                host.modalSvc.membersDetails4NewTeam.tempId = host.modalSvc.membersDetails4NewTeam.id;
                host.modalSvc.membersDetails4NewTeam.id = element.id;
                await this.removedMember(host.modalSvc.membersDetails4NewTeam, team.id, host);
                team.checked = false;
              }
            });
          };

          if (team.memberships_count > 20) {
            this.rosterMemberService.fetchTeamMemberships(team.id, team?.memberships_count)
              .pipe(takeUntil(host.destroy$)).subscribe(async (res) => {
                await removedMemberProcess(res?.memberships);
              });
          } else {
            removedMemberProcess(team?.memberships);
          }
          host.modalSvc.addToTeamBtn = false;
        }
      });
    }
    host.loading = false;
  }

  // ── remove member from team ────────────────────────────────────────────────

  async removedMember(member: any, teamId: string = '', host: IRosterOpsHost): Promise<void> {
    let identity_id = member?.identity_id
      ? member?.identity_id
      : member.default_identity?.id
      ? member.default_identity.id
      : member.default_profile?.identity_id;
    host.allTeamIdsTtl = [];
    host.teams.forEach((team: any, index) => {
      // update teams_names on any membership that matches this identity
      team.memberships.forEach((mem: any) => {
        if (identity_id == mem?.identity_id) {
          if (teamId) {
            delete mem?.teams_names?.[teamId];
          } else if (member?.team_id && member?.teams_names) {
            delete mem?.teams_names?.[member?.team_id];
          }
        }
      });
      // remove from the correct team using .filter() so Angular *ngFor detects the change
      const shouldRemove = (teamId && String(team.id) === String(teamId))
        || (!teamId && member?.team_id && String(member.team_id) === String(team.id));
      if (shouldRemove) {
        const before = team.memberships.length;
        host.teams[index].memberships = team.memberships.filter((m: any) => m.id !== member.id);
        if (host.teams[index].memberships.length < before && host.teams[index].memberships_count > 0) {
          host.teams[index].memberships_count -= 1;
        }
      }
      host.allTeamIdsTtl.push({
        teamId: team.id,
        showMember: host.defaultShowingMembers,
        teamName: team.name,
        memberships_count: team.memberships_count,
      });
    });

    if (teamId) {
      delete host.modalSvc.membersDetails4NewTeam?.teams_names[teamId];
      host.teamsData.members.forEach((mem, ind) => {
        if (
          member?.member_id == mem?.id ||
          member?.id == mem?.id ||
          mem?.default_identity?.id == identity_id
        ) {
          host.teamsData.members[ind].non_admin_unassgined_teams_count -= 1;
          delete host.teamsData.members[ind].teams_names[teamId];
        }
      });

      if (host.filterData != '') {
        host.filterData.forEach((mem) => {
          if (identity_id == mem?.default_identity?.id) {
            mem.non_admin_unassgined_teams_count -= 1;
            delete mem.teams_names[teamId];
          }
        });
      }
    } else if (teamId == '') {
      host.teamsData.members.forEach((mem, ind) => {
        if (identity_id == mem?.default_identity?.id) {
          let tm_id = member?.team_id;
          delete host.teamsData.members[ind]?.teams_names[tm_id];
          host.teamsData.members[ind].non_admin_unassgined_teams_count -= 1;
        }
      });
      if (host.filterData != '') {
        host.filterData.forEach((mem) => {
          if (identity_id == mem?.default_identity?.id) {
            let tm_id = member?.team_id;
            mem.non_admin_unassgined_teams_count -= 1;
            delete mem.teams_names[tm_id];
          }
        });
      }
    }

    const removeObs = this.rosterMemberService.removeMemberFromTeam(identity_id, member.id);

    removeObs
      .pipe(takeUntil(host.destroy$))
      .subscribe(
        async () => {
          Swal.fire({ title: 'Nice!', text: 'The Member was removed successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
          host.modalSvc.addTeamPopup = false;
          identity_id = null;
          host.modalSvc.membersDetails4NewTeam = null;
        },
        (err) => {
          const msg = err?.error?.errors || err?.error?.message || err?.message || 'Failed to remove member from team.';
          Swal.fire({ title: 'Error', text: msg, icon: 'error' });
          host.loading = false;
        }
      );
  }

  // ── add new roster member by email ────────────────────────────────────────

  addNewTeamMate(host: IRosterOpsHost): void {
    const emailReg = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (host.newUseremail.match(emailReg)) {
      host.teams.forEach((team, index) => {
        if (team.name === 'Unassigned') {
          this.rosterMemberService.addNewMember(team.id, host.newUseremail)
            .pipe(takeUntil(host.destroy$)).subscribe((res: any) => {
              res.team.memberships.forEach((mem) => {
                if (host.newUseremail === mem.email) {
                  mem['non_admin_unassgined_teams_count'] = 0;
                  host.teamsData.members.push(mem);
                }
              });
              host.teams[index].memberships = res?.team?.memberships;
              host.teams[index].memberships_count = res?.team?.memberships_count;
              host.modalSvc.showTeamModal = false;
              host.newUseremail = '';
            });
        }
      });
    }
  }

  // ── set invite state for adding all roster members to a team ──────────────

  addAllMembers(team: any, host: IRosterOpsHost): void {
    host.modalSvc.invite = true;
    host.team_name = team?.name;
    host.modalSvc.inviteTeamId = team.id;
    const data: any = { members_attributes: {} };
    host.teamsData.members.forEach((el: any, index: number) => {
      let memDetails: any = {};
      if (el?.default_identity?.email || el?.default_profile?.email) {
        memDetails = {
          email: el?.default_identity?.email || el?.default_profile?.email,
          first_name: el?.default_identity?.first_name || el?.default_profile?.first_name,
          last_name: el?.default_identity?.last_name || el?.default_profile?.last_name,
          role_id: 3,
        };
      }
      data['members_attributes'][index] = memDetails;
    });
  }
}
